import React from "react";
import { IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { DndProvider } from "react-dnd";
import { MultiBackend, getBackendOptions } from "@minoru/react-dnd-treeview";

import { useTranslation } from "@/lib/i18n";
import { Location } from "@/lib/api";
import {
  DragHandle,
  SortableItem,
  SortableList,
} from "@/features/admin/components/sortable-list";
import {
  useDashboardLocationsQuery,
  useReorderDashboardLocationsMutation,
} from "@/features/admin/features/dashboards";
import { useAdminDashboard } from "@/features/admin/hooks";
import { showToast } from "@/features/shared/components";
import { toMoved } from "@/lib/array";

import EditLocationMenu from "./edit-location-menu";
import styles from "./locations-list.module.css";

const sameOrder = (a: Location[], b: Location[]) =>
  a.length === b.length && a.every((location, index) => location.id === b[index]?.id);

const formatCoordinate = (value: number | null | undefined) =>
  value === null || value === undefined ? "-" : String(value);

const LocationsList = () => {
  const { t } = useTranslation();
  const dashboard = useAdminDashboard();
  const locationsQuery = useDashboardLocationsQuery(dashboard.id);
  const reorderMutation = useReorderDashboardLocationsMutation();

  const [ordered, setOrdered] = React.useState<Location[]>([]);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [locationId, setLocationId] = React.useState<string | null>(null);

  const serverLocations = locationsQuery.data;
  const isReordering = reorderMutation.isPending;

  // Keep the local (drag) order in sync with the server, but never while a
  // reorder is being persisted so the optimistic order does not flicker back.
  React.useEffect(() => {
    if (!isReordering && serverLocations) {
      setOrdered(serverLocations);
    }
  }, [serverLocations, isReordering]);

  const handleReorder = React.useCallback((fromIndex: number, toIndex: number) => {
    setOrdered((previous) => toMoved(previous, fromIndex, toIndex));
  }, []);

  const handleReorderEnd = React.useCallback(() => {
    if (sameOrder(ordered, serverLocations ?? [])) return;

    reorderMutation.mutate(
      { dashboardId: dashboard.id, ids: ordered.map((location) => location.id) },
      {
        onError: () =>
          showToast(t("admin.dashboard_settings.location_settings.list.reorder_error")),
      },
    );
  }, [ordered, serverLocations, reorderMutation, dashboard.id, t]);

  const selectedLocation = React.useMemo<Location | null>(
    () => ordered.find((location) => location.id === locationId) ?? null,
    [ordered, locationId],
  );

  const translationKey = "admin.dashboard_settings.location_settings.fields";

  if (ordered.length === 0) {
    return (
      <p className={styles.empty}>
        {t("admin.dashboard_settings.location_settings.list.empty")}
      </p>
    );
  }

  return (
    <DndProvider backend={MultiBackend} options={getBackendOptions()}>
      <div className={styles.header}>
        <span />
        <span>{t(`${translationKey}.name.label`)}</span>
        <span>{t(`${translationKey}.stationCode.label`)}</span>
        <span>{t(`${translationKey}.kmds.label`)}</span>
        <span>{t(`${translationKey}.lat.label`)}</span>
        <span>{t(`${translationKey}.lon.label`)}</span>
        <span />
      </div>
      <SortableList
        onReorder={handleReorder}
        onReorderEnd={handleReorderEnd}
        groupId="dashboard-locations"
      >
        {ordered.map((location, index) => (
          <SortableItem key={location.id} index={index} className={styles.row}>
            <DragHandle />
            <span className={styles.name}>{location.name}</span>
            <span>{location.knmi_station_code ?? "-"}</span>
            <span>{location.kmds_code ?? "-"}</span>
            <span>{formatCoordinate(location.latitude)}</span>
            <span>{formatCoordinate(location.longitude)}</span>
            <IconButton
              aria-label="edit"
              size="small"
              onClick={(event) => {
                setAnchorEl(event.currentTarget);
                setLocationId(location.id);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </SortableItem>
        ))}
      </SortableList>
      <EditLocationMenu
        anchorEl={anchorEl}
        location={selectedLocation}
        onClose={() => {
          setAnchorEl(null);
          setLocationId(null);
        }}
      />
    </DndProvider>
  );
};

export default LocationsList;
